import { Component, EventEmitter, Input, Output, ViewChild, OnInit, OnDestroy, OnChanges, SimpleChanges, Injector } from '@angular/core';
import { Subscription, Observable, distinctUntilChanged } from 'rxjs';
import { BaseComponent } from 'src/app/common/base/base.component';
import { WFComponent } from 'src/app/components/workflow/workflow';
import { ClEditorComponent } from 'src/app/controls/checklist-components/cl-editor/cl-editor.component';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { ProcedureSignatureType } from 'src/app/components/procedure/enums/procedure-signature-type.enum';
import { WfTaskLocalResource, WfSignature } from 'src/app/components/workflow/workflow';
import {
  ChecklistTemplate, ChecklistTemplateStatusEnum,
} from '../../../checklists';
import { ChecklistBuilderService } from '../../checklist-builder.service';
import { ChecklistBuilderTaskMoreComponent } from './checklist-builder-task-more/checklist-builder-task-more.component';
import { Action, ComponentType } from 'src/app/common/enumerations/enumerations';
import { ChecklistBuilderAnnotationEditComponent } from '../../checklist-builder-annotations/checklist-builder-annotation-edit/checklist-builder-annotation-edit.component';
import { ChecklistBuilderAnnotationsComponent } from '../../checklist-builder-annotations/checklist-builder-annotations.component';
import { WFError } from '../../checklist-builder.component';
import { PlaceholdersService } from 'src/app/services/placeholders/placeholders.service';

@Component({
  selector: 'checklist-builder-task',
  templateUrl: './checklist-builder-task.component.html',
  styleUrls: ['./checklist-builder-task.component.scss'],
})
export class ChecklistBuilderTaskComponent
  extends BaseComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input() task!: WfTaskLocalResource;
  @Input() currentVersion?: ChecklistTemplate;
  @Input() disabled: boolean = false;

  @Output() deletedTask = new EventEmitter();
  @Output() updated = new EventEmitter();
  @Output() duplicated = new EventEmitter();
  @Output() errorsDetected = new EventEmitter<WFError[]>();
  @Output() itemCountChanged = new EventEmitter<number>();

  @ViewChild(ChecklistBuilderAnnotationsComponent) public annotations?: ChecklistBuilderAnnotationsComponent;
  @ViewChild(ChecklistBuilderTaskMoreComponent) public moreinfo!: ChecklistBuilderTaskMoreComponent;

  editing!: boolean;
  tmpName!: string;

  color!: string;
  checklistTemplateStatusEnum = ChecklistTemplateStatusEnum;
  expanded!: boolean;
  errorMessage!: string;
  subAppState!: Subscription;

  isRCP!: boolean;
  s: WfSignature = {};

  components!: WFComponent[];
  components$!: Observable<WFComponent[]>;
  componentsSubs!: Subscription;

  taskLoading = true;

  hasAnnotations?: boolean;

  componentsToSwapText = [
    ComponentType.BRATOI,
    ComponentType.CheckboxAndRadio,
    ComponentType.CheckboxAndTextInput,
    ComponentType.ScheduleAndCheckbox,
  ];

  constructor(
    protected override injector: Injector,
    public service: ChecklistBuilderService,
    private placeholdersService: PlaceholdersService,
  ) {
    super(injector);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.disabled = this.disabled || (this.task.logID ?? 0) > 0;
  }

  ngOnInit() {
    this.loadComponents();
    this.isRCP = this.currentVersion?.documentTypeID == 200;
    this.refreshSignature();
  }

  loadComponents() {
    this.components$ = this.store.select((state) => state.Components.data);
    this.componentsSubs = this.components$.pipe(distinctUntilChanged()).subscribe((data) => {
      if (data.length) this.components = data;
    });
  }

  refreshSignature() {
    if (this.task && !this.task.component)
      this.task.component = this.components.find(
        (c) => c.id == this.task.componentID
      );
    this.getSignature(this.task);
    if (this.annotations) {
      setTimeout(() => {
        this.hasAnnotations = this.annotations?.hasAnnotations;
      }, 100);
    }
    this.taskLoading = false;
  }

  getSignature(t: WfTaskLocalResource) {
    if (t) {
      this.s = {
        id: t.id,
        type: t.componentID,
        component: this.components?.find((c) => c.id == t.componentID),
        number: t.component?.numericHeader
          ? (t.name ?? 0 + 1).toString()
          : null,
        required: t.wfTasks?.[0]?.required,
        name: t.name,
        code: t.code,
        question: t.name3,
        question2: t.name2,
        question3: t.name4,
        options: this.utils.JSONparse(t.options)
          ? this.utils.JSONparse(t.options)
          : [],
        condition: t.condition,
        sigCondition: t.sigCondition,
        configuration: t.configuration,
        scheduleTypeId: t.scheduleType,
        scheduleResourceId: t.scheduleResourceId,
        resourceId: t.resourceId,
        locationName: t.resourceName,
        taskId: 0,
        order: t.order,
        disabled: true,
        enabled: true,
        stepId: t.wfStepLocalID,
        roles: t.wfRoleLocals?.map((x) => x.roleID),
        roleCodes: t.wfRoleLocals?.map((x) => x.role?.code).join('/'),
        approveAction:
          t.wfActionLocals?.find((x) => x.action == Action.Approve) != null,
        approveActionId: t.wfActionLocals?.find((x) => x.action == Action.Approve)
          ?.id,
        unapproveAction:
          t.wfActionLocals?.find((x) => x.action == Action.Unapprove) != null,
        unapproveActionId: t.wfActionLocals?.find((x) => x.action == Action.Unapprove)
          ?.id,
        disapproveAction:
          t.wfActionLocals?.find((x) => x.action == Action.Disapprove) != null,
        disapproveActionId: t.wfActionLocals?.find(
          (x) => x.action == Action.Disapprove
        )?.id,
        invalid: !!t.errors?.length,
        wfTaskLocal: t,
      };
      if (this.s?.configuration) {
        const config = JSON.parse(this.s.configuration);
        this.color = config?.color ?? 'primary';
      }
    }

    return this.s;
  }

  /**
   * Opens the shared task editor and restores this checklist's placeholder context when it closes.
   * @param disabled Whether the editor is opened in view-only mode.
   */
  edit(disabled: boolean) {
    const s = this.s;
    const editor = this.dialog.open(ClEditorComponent, {
      maxHeight: window.innerHeight + 'px',
      maxWidth: '100%',
      minWidth: '60%',
      panelClass: 'draggable-dialog',
      data: {
        s,
        formType: this.currentVersion?.documentType?.id,
        serialNo: this.currentVersion?.serialNo,
        roles: this.task.wfRoleLocals?.map((x) => x.role),
        checklistTemplate: this.currentVersion,
        disabled,
        checklistBuilderMode: true,
      },
      disableClose: true,
      autoFocus: false,
      hasBackdrop: true,
    });
    editor.afterClosed().subscribe(async (data) => {
      // Other builder dialogs share this service, so restore the selected template resource after closing.
      if (this.currentVersion?.resource)
        await this.placeholdersService.setKeyValuePairs(this.currentVersion.resource, true);
      if (data) {
        this.service
          .updateWFTaskLocal(data)
          .subscribe((data: WfTaskLocalResource) => {
            this.task = data;
            this.refreshSignature();
            this.updated.emit(this.task);
          });
      }
    });
  }

  duplicate() {
    const yesno = this.dialog.open(YesNoDialogComponent, {
      width: '400px',
      data: {
        message: this.getMessage('GenChklistBldr_DuplicateTaskConfimation')
          .description,
        icon: 'question',
      },
    });
    yesno.afterClosed().subscribe((data) => {
      if (data && this.task.id) {
        this.service.duplicateTask(this.task.id).subscribe(
          (task) => {
            task.component = this.components.find(
              (c) => c.id == task.componentID
            );
            this.duplicated.emit(task);
          },
          (error) => {
            console.log(error);
          }
        );
      }
    });
  }

  delete() {
    const yesno = this.dialog.open(YesNoDialogComponent, {
      width: '400px',
      data: {
        message: this.getMessage('GenChklistBldr_DeleteTaskConfimation')
          .description,
        icon: 'stop',
      },
    });
    yesno.afterClosed().subscribe((data) => {
      if (data) {
        this.service.deleteTask(this.task).toPromise().then(() => {
          this.deletedTask.emit(this.task);
        },
          (error) => {
            console.log(error);
          }
        );
      }
    });
  }

  cancel() {
    this.editing = false;
  }

  changeRequired(e: any) {
    const required = e.checked;
    if (this.task.wfTasks?.length) {
      this.task.wfTasks[0].required = required;
      this.service.saveRequired(this.task.wfTasks[0]).subscribe((data) => {
        this.task = data;
        this.updated.emit(this.task);
      });
    }
  }

  change(e: any) {
    let configuration = this.utils.JSONparse(this.s.configuration);
    if (!configuration) {
      configuration = {};
    }
    const data = this.utils.JSONparse(e).data;
    if (data) {
      configuration.data = data;
    }
    const widths = JSON.parse(e).widths;
    if (widths) {
      configuration.widths = widths;
    }
    this.s.configuration = JSON.stringify(configuration);
    const wf = { s: this.s, roles: this.task.wfRoleLocals };
    this.service
      .updateWFTaskLocal(wf)
      .subscribe((data: WfTaskLocalResource) => {
        this.task = data;
        this.refreshSignature();
        this.updated.emit(this.task);
      });
  }

  annotate() {
    const editor = this.dialog.open(ChecklistBuilderAnnotationEditComponent, {
      width: '80vw',
      maxHeight: '90vh',
      data: {
        s: this.s,
        task: this.task,
        annotation: null,
        currentVersion: this.currentVersion,
        templateId: this.currentVersion?.id,
      },
      autoFocus: false,
      disableClose: true,
      hasBackdrop: true,
    });
    editor.afterClosed().subscribe(async (data) => {
      if (data) {
        this.task = data;
        this.refreshSignature();
      }
    });
  }

  hasErrors() {
    const taskError = this.service.hasTaskErrors(
      this.task,
      this.service.getChecklistExecStatuses(this.currentVersion),
      this.templateTasks()
    );
    if (taskError) {
      this.errorMessage = taskError.errors.join(', ');
      this.errorsDetected.emit([taskError]);
      return true;
    }
    return false;
  }

  /**
   * Every task in the template. Signature conditions reference tasks by Tag from anywhere in the
   * checklist, so validating this task's condition needs the whole set.
   */
  private templateTasks() {
    return this.currentVersion?.wfTable?.wfTableLocal?.wfSectionLocals
      ?.flatMap(s => s.wfStepLocals ?? [])
      ?.flatMap(s => s.wfTaskLocals ?? []);
  }

  isReviewer() {
    if (this.service.procedure) {
      const users = this.service.procedure.procedureSignatures?.filter(
        (x) => x.signatureType == ProcedureSignatureType.Reviewer && !x.signedOn
      );
      return users
        ?.map((x) => x.userID)
        .includes(this.getCurrentUser()?.id ?? 0);
    }
    return false;
  }

  isApprover() {
    if (this.service.procedure) {
      const users = this.service.procedure.procedureSignatures?.filter(
        (x) => x.signatureType == ProcedureSignatureType.Approver && !x.signedOn
      );
      return users
        ?.map((x) => x.userID)
        .includes(this.getCurrentUser()?.id ?? 0);
    }
    return false;
  }

  isPreparer() {
    return this.hasRoles(
      this.currentVersion?.documentType?.templateRolePermissions?.map(
        (x) => x.roleID
      )
    );
  }

  annotationChanged(e: WfTaskLocalResource) {
    this.task = e;
    this.refreshSignature();
  }
}
